import { MarkOutcome } from '@wiztime/core';

/**
 * Quick mark as a format-brush mode (2026-10b §4.1–§4.3). The state lives in memory only
 * (`dayUiStore`): a reload always starts at Off. The transitions here are pure; the store
 * wraps them with the clock and the `quick-lock` learning counter.
 */

export const QuickMarkMode = {
  /** Not active. */
  Off: 'off',
  /** One-shot: returns to Off after one successful mark. */
  Once: 'once',
  /** Locked: marks continuously until an explicit exit. */
  Locked: 'locked',
} as const;
export type QuickMarkMode = (typeof QuickMarkMode)[keyof typeof QuickMarkMode];

/** Double-click window on a tile (§4.2, the Windows default double-click time). */
export const QUICK_MARK_LOCK_MS = 500;
/** Touch long press on a tile locks (§4.2, same as the lane's touch arming). */
export const QUICK_MARK_LONGPRESS_MS = 380;
/** A touch that moves farther than this during the long press cancels it (§4.2). */
export const QUICK_MARK_MOVE_CANCEL_PX = 10;

/**
 * How a tile was activated (§4.2): only pointer activations (mouse, touch) take part in the
 * double-click judgement. Digit keys and Enter/Space never lock by repetition — pressing the
 * same digit twice means "enter, then exit"; locking by keyboard uses the Shift combinations.
 */
export const QuickMarkSource = {
  Pointer: 'pointer',
  /** Enter or Space on a focused tile: like a single click, without the double-click window. */
  Key: 'key',
  /** Digit keys 1–9 (§4.8): one-shot; on Locked they switch the project and stay locked. */
  Digit: 'digit',
} as const;
export type QuickMarkSource = (typeof QuickMarkSource)[keyof typeof QuickMarkSource];

export interface QuickMarkState {
  readonly mode: QuickMarkMode;
  /** Project being stamped; null exactly while Off. */
  readonly projectId: string | null;
  /** Epoch ms of the pointer activation that entered Once: start of the double-click window. */
  readonly onceAt: number | null;
  /**
   * Pointer activations of this tile are ignored until `until`: a double click on a locked
   * tile stays Off, and a double click that switches the locked project stays Locked (§4.2).
   */
  readonly ignore: { readonly projectId: string; readonly until: number } | null;
}

export const QUICK_MARK_OFF: QuickMarkState = {
  mode: QuickMarkMode.Off,
  projectId: null,
  onceAt: null,
  ignore: null,
};

const once = (projectId: string, onceAt: number | null): QuickMarkState => ({
  mode: QuickMarkMode.Once,
  projectId,
  onceAt,
  ignore: null,
});

const locked = (projectId: string): QuickMarkState => ({
  mode: QuickMarkMode.Locked,
  projectId,
  onceAt: null,
  ignore: null,
});

/**
 * A tile of project `projectId` was activated (§4.2 event table). `detail` is
 * `MouseEvent.detail` for pointer activations (a system double click, `detail >= 2`, locks
 * even outside the 500 ms window); 0 otherwise.
 */
export function activateQuickMark(
  state: QuickMarkState,
  projectId: string,
  source: QuickMarkSource,
  detail: number,
  now: number,
): QuickMarkState {
  // After "click the locked tile → Off", the second half of that double click arrives as
  // another pointer activation of the same tile: it must keep the mode Off, not re-enter Once.
  if (
    source === QuickMarkSource.Pointer &&
    state.ignore !== null &&
    state.ignore.projectId === projectId &&
    now < state.ignore.until
  ) {
    return state;
  }
  const pointer = source === QuickMarkSource.Pointer;
  if (state.mode === QuickMarkMode.Off) return once(projectId, pointer ? now : null);
  if (state.mode === QuickMarkMode.Once) {
    if (state.projectId !== projectId) return once(projectId, pointer ? now : null);
    const inWindow = state.onceAt !== null && now - state.onceAt <= QUICK_MARK_LOCK_MS;
    if (pointer && (detail >= 2 || inWindow)) return locked(projectId);
    return QUICK_MARK_OFF;
  }
  // Locked: another tile switches the project and stays locked (the lock belongs to the
  // whole session, §4.2); the digit key of the current project does too (§4.8); a click or
  // Enter on the current tile exits.
  if (state.projectId !== projectId) {
    // A habitual double click on the new tile must not read as "switch, then click the locked
    // tile": the trailing pointer activation inside the double-click window is ignored, so the
    // mode stays Locked on the new project.
    return {
      ...locked(projectId),
      ignore: pointer ? { projectId, until: now + QUICK_MARK_LOCK_MS } : null,
    };
  }
  if (source === QuickMarkSource.Digit) return state;
  return {
    ...QUICK_MARK_OFF,
    ignore: pointer ? { projectId, until: now + QUICK_MARK_LOCK_MS } : null,
  };
}

/** Lock directly (§4.2): Shift+digit, Shift+Enter/Space on a tile, or the touch long press. */
export function lockQuickMark(projectId: string): QuickMarkState {
  return locked(projectId);
}

/**
 * A marking command finished with `outcome` (§4.3): Once is consumed — back to Off — only on
 * `Created`; every other outcome, and the Locked mode, keep the state.
 */
export function settleQuickMark(state: QuickMarkState, outcome: MarkOutcome): QuickMarkState {
  if (state.mode !== QuickMarkMode.Once) return state;
  if (outcome !== MarkOutcome.Created) return state;
  return QUICK_MARK_OFF;
}
